import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getSessionToken } from "../../hooks/useSession.js";
import { cn } from "../../lib/cn.js";
import { patchSurelcTask, type StaffChoice } from "../agent-detail/SurelcOnboardingPanel.js";
import { Button, Card, EmptyState, LoadingStatus, PageHeader, Select, Skeleton } from "../ui/index.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface TaskRow {
  id: string;
  body: string;
  dueAt: string | null;
  completedAt: string | null;
  createdAt: string;
  overdue: boolean;
  assignee: { id: string; name: string } | null;
  author: { id: string; name: string } | null;
  agent: { id: string; name: string; email: string };
}

interface AttentionRow {
  agent: { id: string; name: string; email: string };
  kind: "producer" | "sync";
  status: string;
  detail: string;
  at: string;
}

interface TasksResponse {
  tasks: TaskRow[];
  attention: AttentionRow[];
  staff: StaffChoice[];
  viewerId: string | null;
}

type StatusFilter = "open" | "done" | "all";

const STATUS_LABEL: Record<string, string> = {
  dry_run: "Dry run only",
  needs_staff: "Needs staff",
  failed: "Failed",
  sync_error: "SureLC sync error",
};

function formatDay(value: string | null): string {
  if (!value) return "No due date";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "No due date" : parsed.toLocaleDateString();
}

export function OnboardingTasks() {
  const token = getSessionToken();
  const [status, setStatus] = useState<StatusFilter>("open");
  const [mineOnly, setMineOnly] = useState(false);
  const [data, setData] = useState<TasksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ status });
      if (mineOnly) params.set("assignee", "me");
      const response = await fetch(`${API_BASE}/api/surelc-tasks?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await response.json().catch(() => null);
      if (!response.ok || !json?.success) throw new Error(json?.error || "Unable to load onboarding tasks");
      setData(json.data as TasksResponse);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load onboarding tasks");
    } finally {
      setLoading(false);
    }
  }, [mineOnly, status, token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function retryProducer(agentId: string) {
    if (!token) return;
    setBusy(agentId);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/surelc-tasks/producer-retry/${encodeURIComponent(agentId)}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await response.json().catch(() => null);
      if (!response.ok || !json?.success) throw new Error(json?.error || "Unable to retry SureLC producer");
      await load();
    } catch (retryError) {
      setError(retryError instanceof Error ? retryError.message : "Unable to retry SureLC producer");
    } finally {
      setBusy(null);
    }
  }

  async function update(task: TaskRow, body: { assigneeId?: string | null; completed?: boolean }) {
    if (!token) return;
    setBusy(task.id);
    setError(null);
    try {
      await patchSurelcTask(token, task.agent.id, task.id, body);
      await load();
    } catch (updateError) {
      setError(updateError instanceof Error ? updateError.message : "Unable to update task");
    } finally {
      setBusy(null);
    }
  }

  const tasks = data?.tasks ?? [];
  const overdue = tasks.filter((task) => task.overdue).length;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Onboarding tasks"
        description="Follow-ups staff assigned on an agent's SureLC onboarding, plus agents whose SureLC setup is waiting on Altura. Add a task from the SureLC panel on any agent."
      />

      <section className="space-y-3" aria-labelledby="attention-title">
        <h2 id="attention-title" className="text-lg font-semibold text-content-primary">
          Waiting on Altura
        </h2>
        {loading && !data ? (
          <Skeleton className="h-20 w-full rounded-card" />
        ) : data?.attention.length ? (
          <ul className="grid gap-2 md:grid-cols-2">
            {data.attention.map((row) => (
              <li
                key={`${row.kind}-${row.agent.id}`}
                className="rounded-xl border border-status-rejected-border bg-status-rejected-bg px-4 py-3 text-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <Link
                    to={`/dashboard/agents/${row.agent.id}`}
                    className="focus-ring min-w-0 font-semibold text-content-primary hover:underline"
                  >
                    {row.agent.name}
                  </Link>
                  {row.kind === "producer" ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy === row.agent.id}
                      onClick={() => void retryProducer(row.agent.id)}
                    >
                      {busy === row.agent.id ? "Queuing…" : "Retry"}
                    </Button>
                  ) : null}
                </div>
                <span className="mt-1 block text-xs font-semibold text-status-rejected-text">
                  {STATUS_LABEL[row.status] ?? row.status}
                </span>
                <span className="mt-1 block text-xs text-content-secondary">{row.detail}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-content-muted">No SureLC producer or sync problems right now.</p>
        )}
      </section>

      <section className="space-y-3" aria-labelledby="tasks-title">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="tasks-title" className="text-lg font-semibold text-content-primary">
            Tasks
            {status === "open" && overdue > 0 ? (
              <span className="ml-2 text-sm font-semibold text-status-rejected-text">{overdue} overdue</span>
            ) : null}
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            {(["open", "done", "all"] as const).map((value) => (
              <Button
                key={value}
                size="sm"
                variant={status === value ? "primary" : "secondary"}
                aria-pressed={status === value}
                onClick={() => setStatus(value)}
              >
                {value === "open" ? "Open" : value === "done" ? "Done" : "All"}
              </Button>
            ))}
            <Button
              size="sm"
              variant={mineOnly ? "primary" : "secondary"}
              aria-pressed={mineOnly}
              onClick={() => setMineOnly((current) => !current)}
            >
              Assigned to me
            </Button>
          </div>
        </div>

        {error ? (
          <p role="alert" className="rounded-xl border border-status-rejected-border bg-status-rejected-bg px-3 py-2 text-sm text-status-rejected-text">
            {error}
          </p>
        ) : null}

        {loading && !data ? (
          <div role="status" aria-busy="true" className="space-y-2">
            <LoadingStatus label="Loading onboarding tasks" />
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-16 w-full rounded-xl" />
            ))}
          </div>
        ) : tasks.length === 0 ? (
          <EmptyState
            title={status === "open" ? "Nothing open" : "No tasks here"}
            description="Tasks come from notes on an agent's SureLC panel that have an assignee or a due date."
          />
        ) : (
          <ul className="space-y-2">
            {tasks.map((task) => (
              <li key={task.id}>
                <Card
                  padding="md"
                  className={cn(
                    "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
                    task.overdue && "border-status-rejected-border",
                    task.completedAt && "opacity-70"
                  )}
                >
                  <div className="min-w-0 space-y-1">
                    <Link
                      to={`/dashboard/agents/${task.agent.id}`}
                      className="text-sm font-semibold text-accent hover:underline"
                    >
                      {task.agent.name}
                    </Link>
                    <p className={cn("text-sm text-content-primary", task.completedAt && "line-through")}>{task.body}</p>
                    <p className="flex flex-wrap gap-x-3 text-xs text-content-muted">
                      <span className={cn(task.overdue && "font-semibold text-status-rejected-text")}>
                        Due {formatDay(task.dueAt)}
                        {task.overdue ? " · overdue" : ""}
                      </span>
                      {task.author ? <span>From {task.author.name}</span> : null}
                      {task.completedAt ? <span>Done {formatDay(task.completedAt)}</span> : null}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <Select
                      value={task.assignee?.id ?? ""}
                      disabled={busy === task.id}
                      aria-label={`Assignee for task on ${task.agent.name}`}
                      onChange={(event) => void update(task, { assigneeId: event.target.value || null })}
                      className="w-44"
                    >
                      <option value="">Unassigned</option>
                      {(data?.staff ?? []).map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.id === data?.viewerId ? `${user.name} (me)` : user.name}
                        </option>
                      ))}
                    </Select>
                    <Button
                      size="sm"
                      variant={task.completedAt ? "ghost" : "secondary"}
                      disabled={busy === task.id}
                      onClick={() => void update(task, { completed: !task.completedAt })}
                    >
                      {task.completedAt ? "Reopen" : "Mark done"}
                    </Button>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
